import { Task, TaskStatus, Priority } from '@/types';
import { OverwhelmStatus, RecoveryPlan, RecoveryTask } from '../types';

// Logic to detect if a student is overwhelmed
export const detectOverdueTasks = (tasks: Task[]): OverwhelmStatus => {
    const now = new Date();
    console.log('[RecoveryEngine] Detecting overwhelm. Now:', now.toISOString(), 'Tasks:', tasks.length);

    const overdueTasks = tasks.filter(t => {
        if (t.status === TaskStatus.DONE) return false;
        const due = new Date(t.dueDate);
        const isOverdue = due < now;
        console.log('[RecoveryEngine] Task:', t.title, 'Due:', t.dueDate, 'Status:', t.status, 'Overdue:', isOverdue);
        return isOverdue;
    });

    const upcomingCriticalTasks = tasks.filter(t => {
        if (t.status === TaskStatus.DONE) return false;
        const due = new Date(t.dueDate);
        const hoursUntilDue = (due.getTime() - now.getTime()) / (1000 * 60 * 60);
        const isCritical = hoursUntilDue > 0 && hoursUntilDue < 48 && t.priority === Priority.HIGH;
        console.log('[RecoveryEngine] Task:', t.title, 'HoursUntil:', hoursUntilDue.toFixed(1), 'Priority:', t.priority, 'Critical:', isCritical);
        return isCritical;
    });

    const score = calculatePanicScore(overdueTasks.length, upcomingCriticalTasks.length);
    const reasons: string[] = [];

    if (overdueTasks.length > 2) reasons.push(`${overdueTasks.length} tasks are overdue`);
    if (upcomingCriticalTasks.length > 0) reasons.push(`${upcomingCriticalTasks.length} critical deadlines within 48h`);

    console.log('[RecoveryEngine] Overdue:', overdueTasks.length, 'Critical:', upcomingCriticalTasks.length, 'Score:', score, 'isOverwhelmed:', score > 50);

    // Trigger threshold: Score > 50 implies "Recovery Mode" recommended
    return {
        isOverwhelmed: score > 50,
        score,
        reason: reasons,
        overdueTasks,
        upcomingCriticalTasks
    };
};

const calculatePanicScore = (overdueCount: number, criticalCount: number): number => {
    let score = 0;
    score += overdueCount * 20; // Each overdue task is stressful
    score += criticalCount * 15; // Upcoming deadlines significantly add to load
    return Math.min(score, 100);
};

// Generates a linear "Survival Plan"
export const generateRecoveryPlan = (
    userId: string,
    overdueTasks: Task[],
    upcomingTasks: Task[]
): RecoveryPlan => {

    // 1. Sort by "Damage Control": Hard Deadlines (Upcoming) > High Value Overdue
    const critical = [...upcomingTasks].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    const overdue = [...overdueTasks].sort((a, b) => {
        // Sort overdue by priority first
        const prioMap = { [Priority.HIGH]: 3, [Priority.MEDIUM]: 2, [Priority.LOW]: 1 };
        return prioMap[b.priority] - prioMap[a.priority];
    });

    // 2. Interleave Breaks (The "Human" Element)
    const rawSequence = [...critical, ...overdue];
    const features: RecoveryTask[] = [];

    rawSequence.forEach((task, index) => {
        const isFirst = index === 0;

        // Add the task
        features.push({
            ...task,
            recoveryAction: 'focus',
            originalDueDate: task.dueDate,
            recommendedDuration: task.estimated_minutes || 60,
            isLocked: !isFirst // Only the first task is unlocked
        });

        // Add a break after every task or every 2 hours of work
        // For simplicity in MVP, add a small break task after every real task
        features.push({
            id: `break-${task.id}`,
            title: "Brain Break",
            description: "Step away. Drink water. Reset.",
            subject: task.subject, // Type compatibility
            priority: Priority.LOW,
            status: TaskStatus.TO_STUDY,
            dueDate: new Date().toISOString(),
            createdAt: Date.now(),
            subtasks: [],
            resources: [],
            recoveryAction: 'break',
            originalDueDate: new Date().toISOString(),
            recommendedDuration: 15,
            isLocked: true
        });
    });

    return {
        id: `plan-${Date.now()}`,
        userId,
        createdAt: new Date().toISOString(),
        originalTaskCount: rawSequence.length,
        tasks: features,
        currentStepIndex: 0,
        status: 'active'
    };
};
